/**
 * AcumenClickInterceptor — ACUMEN-10 RELAY
 * 
 * When a command bar tool is active, intercepts clicks anywhere in the app
 * and fires the appropriate Acumen action on the clicked element.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAcumenCommand } from '../../context/AcumenCommandContext';
import { useAcumen } from '../../context/AcumenContext';
import { useTheme } from '../../context/ThemeContext';

const TOOL_PROMPTS = {
  vibe:     (text, tag) => `I clicked on a ${tag} element in the app. Here is its content: "${text}". Have a conversation with me about this — what insights, questions, or guidance do you have?`,
  comply:   (text, tag) => `Check this ${tag} content for FAR/VAAR compliance issues, missing citations, and regulatory gaps: "${text}"`,
  help:     (text, tag) => `Explain this ${tag} element in plain English for a federal acquisition professional: "${text}"`,
  generate: (text, tag) => `Generate improved or expanded formal government acquisition language for this ${tag} element: "${text}"`,
  context:  null, // handled separately — captures, doesn't query
};

function extractElementContent(el) {
  // Walk up to find meaningful content
  let target = el;
  for (let i = 0; i < 5; i++) {
    const text = (target.innerText || target.textContent || target.value || '').trim();
    if (text.length > 5) return { text: text.slice(0, 800), tag: target.tagName.toLowerCase(), el: target };
    if (target.parentElement) target = target.parentElement;
    else break;
  }
  return { text: '', tag: el.tagName.toLowerCase(), el };
}

export default function AcumenClickInterceptor() {
  const { activeTool, activeAgent, captureContext } = useAcumenCommand();
  const { query_acumen, sessionContext } = useAcumen();
  const { darkMode } = useTheme();

  const [panel, setPanel] = useState(null); // {x, y, loading, response, mode, text}
  const panelRef = useRef(null);

  const bg = darkMode ? '#1a1f2e' : '#ffffff';
  const border = darkMode ? '#2d3748' : '#e5e7eb';
  const textColor = darkMode ? '#e2e8f0' : '#111827';

  const TOOL_COLORS = {
    vibe: '#3b82f6', comply: '#ef4444', help: '#f59e0b',
    generate: '#8b5cf6', context: '#10b981',
  };

  const dismiss = useCallback(() => setPanel(null), []);

  // Close panel on click outside
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        dismiss();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dismiss]);

  // Escape to dismiss
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dismiss]);

  // Main click interceptor
  useEffect(() => {
    if (!activeTool) return;

    const handleClick = async (e) => {
      // Don't intercept clicks on our own UI
      if (e.target.closest('[data-acumen-ui]')) return;
      if (e.target.closest('[data-no-acumen]')) return;

      e.preventDefault();
      e.stopPropagation();

      const { text, tag } = extractElementContent(e.target);
      if (!text) return;

      const x = Math.min(e.clientX, window.innerWidth - 420);
      const y = Math.min(e.clientY + 12, window.innerHeight - 300);
      const color = TOOL_COLORS[activeTool] || '#6366f1';

      // CONTEXT mode — capture, don't query
      if (activeTool === 'context') {
        captureContext({ elementType: tag, text: text.slice(0, 200), label: text.slice(0, 40), timestamp: Date.now() });
        setPanel({ x, y, mode: 'context', color, text: text.slice(0, 80), response: `✅ Captured: "${text.slice(0, 60)}${text.length > 60 ? '…' : ''}"`, loading: false });
        setTimeout(() => setPanel(null), 3000);
        return;
      }

      const promptFn = TOOL_PROMPTS[activeTool];
      if (!promptFn) return;

      setPanel({ x, y, mode: activeTool, color, text: text.slice(0, 60), loading: true, response: null });

      try {
        const result = await query_acumen(
          promptFn(text, tag),
          activeTool,
          { clicked_element: tag, clicked_text: text.slice(0, 200) }
        );
        setPanel(prev => prev ? { ...prev, loading: false, response: result } : null);
      } catch (_) {
        setPanel(prev => prev ? { ...prev, loading: false, response: 'Acumen is temporarily unavailable.' } : null);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [activeTool, activeAgent, query_acumen, captureContext, sessionContext]);

  if (!panel) return null;

  return (
    <div
      ref={panelRef}
      data-acumen-ui="true"
      style={{
        position: 'fixed',
        left: panel.x,
        top: panel.y,
        width: 400,
        maxHeight: 320,
        background: bg,
        border: `2px solid ${panel.color}40`,
        borderRadius: 14,
        boxShadow: `0 8px 40px rgba(0,0,0,0.25), 0 0 0 1px ${panel.color}20`,
        zIndex: 10001,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px 8px',
        borderBottom: `1px solid ${border}`,
        background: `${panel.color}12`,
      }}>
        <span style={{
          background: panel.color, color: '#fff',
          fontSize: 10, fontWeight: 800, padding: '2px 8px',
          borderRadius: 20, letterSpacing: '0.05em',
        }}>
          {panel.mode.toUpperCase()}
        </span>
        <span style={{ fontSize: 11, color: darkMode ? '#64748b' : '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
          "{panel.text}{panel.text?.length >= 60 ? '…' : ''}"
        </span>
        <button onClick={dismiss} data-acumen-ui="true" style={{
          background: 'none', border: 'none', color: darkMode ? '#64748b' : '#9ca3af',
          cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1,
        }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px', overflowY: 'auto', flex: 1, color: textColor, fontSize: 13, lineHeight: 1.65 }}>
        {panel.loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: darkMode ? '#64748b' : '#9ca3af' }}>
            <span style={{
              width: 14, height: 14, borderRadius: '50%',
              border: `2px solid ${panel.color}30`, borderTopColor: panel.color,
              display: 'inline-block',
              animation: 'acumen-intercept-spin 0.7s linear infinite',
            }} />
            Acumen is analyzing...
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{panel.response}</div>
        )}
      </div>

      {/* Footer — open in full palette */}
      {!panel.loading && panel.response && panel.mode !== 'context' && (
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${border}` }}>
          <button
            data-acumen-ui="true"
            style={{
              background: 'none', border: `1px solid ${panel.color}40`,
              borderRadius: 8, padding: '4px 12px', fontSize: 11,
              color: panel.color, cursor: 'pointer', fontWeight: 600,
            }}
          >
            Open in Acumen ⬡
          </button>
        </div>
      )}

      <style>{`@keyframes acumen-intercept-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
